import type { HomeTodayResponse } from "@gioia365/contracts";
import { Link } from "@tanstack/react-router";
import { useId } from "react";
import { Icon } from "../design/components/Icon";
import { formatCount } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { violationText } from "../leads/card/sequence-texts";
import "./home.css";

// Il riquadro «Sequenze rispettate oggi» della Home (piano M5, Task 22; D19; `renderSequenceSummary` del mock-up): «Le tue sequenze
// rispettate oggi» per chi vede i suoi report, «Sequenze rispettate oggi» con ogni persona per chi vede quelli di tutti
// (`byPerson`); sotto, le non rispettate con il primo motivo, ognuna con il collegamento alla scheda del Lead, e la regola. Il
// giudizio e i motivi li scrive il server: qui si dicono con le parole della scheda (`violationText`).

type Sequences = HomeTodayResponse["sequences"];

/** La spiegazione sotto il riquadro, con la regola dell'azienda (ruling H7). */
function hintOf(t: Translate, rule: Sequences["rule"]): string {
  const friday = rule.fridayOne ? t("homeSequences.fridayHint") : "";
  if (rule.otherAttemptsMinCalls === null) return t("homeSequences.hintOneAttempt", { minutes: rule.firstCallMinutes, friday, first: rule.firstAttemptCalls });
  return t("homeSequences.hint", { minutes: rule.firstCallMinutes, friday, first: rule.firstAttemptCalls, others: rule.otherAttemptsMinCalls });
}

export function SequencesTodayBlock({ sequences }: { sequences: Sequences }) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  const team = sequences.byPerson !== null;
  const none = t("homeSequences.none");
  const score = { respected: formatCount(locale, sequences.respected), total: formatCount(locale, sequences.total) };
  const title =
    sequences.total === 0
      ? t(team ? "homeSequences.teamNone" : "homeSequences.mineNone", { none })
      : t(team ? "homeSequences.team" : "homeSequences.mine", score);
  // Le non rispettate sono le contate meno le rispettate: il server ne nomina al più `HOME_VIOLATIONS_LIMIT`.
  const violated = sequences.total - sequences.respected;
  const hidden = violated - sequences.violations.length;

  return (
    <section className="g-home-sequences" data-tone={violated > 0 ? "warning" : "success"} aria-labelledby={titleId}>
      <p className="g-home-sequences__title">
        <Icon name={violated > 0 ? "alert" : "check"} size="sm" />
        <span id={titleId}>{title}</span>
      </p>
      {sequences.byPerson ? (
        <ul className="g-home-sequences__people">
          {sequences.byPerson.map(({ person, respected, total }) => (
            <li key={person.membershipId}>
              {total === 0
                ? t("homeSequences.personNone", { name: person.displayName, none })
                : t("homeSequences.person", { name: person.displayName, respected: formatCount(locale, respected), total: formatCount(locale, total) })}
            </li>
          ))}
        </ul>
      ) : null}
      {sequences.violations.length > 0 ? (
        <div className="g-home-sequences__violations">
          <p>{t(violated === 1 ? "homeSequences.violatedOne" : "homeSequences.violatedMany")}</p>
          <ul>
            {sequences.violations.map(({ lead, reason }, index) => (
              <li key={`${lead.id}:${index}`}>
                <Link to="/lead/$leadId" params={{ leadId: lead.id }}>
                  {lead.displayName ?? "—"}
                </Link>
                <span aria-hidden="true">·</span>
                <span>{violationText(t, reason)}</span>
              </li>
            ))}
            {hidden > 0 ? <li>{hidden === 1 ? t("homeSequences.moreOne") : t("homeSequences.more", { count: formatCount(locale, hidden) })}</li> : null}
          </ul>
        </div>
      ) : null}
      <p className="g-home-sequences__hint">{hintOf(t, sequences.rule)}</p>
    </section>
  );
}
